
import { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';

interface Announcement {
  id: string;
  title: string;
  content: string;
  created_at: string;
  created_by: string;
  priority: 'low' | 'medium' | 'high';
  is_pinned: boolean;
}

const AnnouncementsSection = () => {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    content: '',
    priority: 'medium' as 'low' | 'medium' | 'high',
    is_pinned: false
  });
  const [submitting, setSubmitting] = useState(false);

  // Check if user is admin (can create announcements)
  const isAdmin = user?.email && [
    'carlos@driveninsurance.com',
    'andrew@driveninsurance.com', 
    'pj@driveninsurance.com',
    'jeremy@driveninsurance.com'
  ].includes(user.email.toLowerCase());

  // Load announcements
  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/announcements-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          action: 'get_announcements'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAnnouncements(data.announcements || []);
      }
    } catch (error) {
      console.error('Error loading announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAnnouncement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAnnouncement.title.trim() || !newAnnouncement.content.trim()) {
      alert('Please fill in both title and content');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/announcements-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          action: 'create_announcement',
          title: newAnnouncement.title.trim(),
          content: newAnnouncement.content.trim(),
          priority: newAnnouncement.priority,
          is_pinned: newAnnouncement.is_pinned,
          created_by: user?.email
        })
      });

      if (response.ok) {
        setNewAnnouncement({
          title: '',
          content: '',
          priority: 'medium',
          is_pinned: false
        });
        setShowCreateForm(false);
        loadAnnouncements();
        alert('Announcement created successfully!');
      } else {
        const errorData = await response.json();
        console.error('Server error:', errorData);
        alert(`Failed to create announcement: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating announcement:', error);
      alert('Error creating announcement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAnnouncement = async (id: string) => {
    if (!confirm('Are you sure you want to delete this announcement?')) {
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/announcements-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          action: 'delete_announcement',
          id: id
        })
      });

      if (response.ok) {
        loadAnnouncements();
        alert('Announcement deleted successfully!');
      } else {
        const errorData = await response.json();
        console.error('Server error:', errorData);
        alert(`Failed to delete announcement: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting announcement:', error);
      alert('Error deleting announcement');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high': return 'ri-error-warning-line';
      case 'medium': return 'ri-information-line';
      case 'low': return 'ri-checkbox-circle-line';
      default: return 'ri-information-line';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading announcements...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-3 sm:space-y-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <i className="ri-megaphone-line text-blue-600"></i>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Company Announcements</h2>
              <p className="text-sm text-gray-600">Stay updated with important company news</p>
            </div>
          </div>
          
          {isAdmin && (
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="flex items-center justify-center px-3 py-2 sm:px-4 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base whitespace-nowrap"
            >
              <i className="ri-add-line mr-1 sm:mr-2"></i>
              <span className="hidden xs:inline">New Announcement</span>
              <span className="xs:hidden">New</span>
            </button>
          )}
        </div>
      </div>

      {/* Create Announcement Form */}
      {showCreateForm && isAdmin && (
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <form onSubmit={handleCreateAnnouncement} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Announcement Title
              </label>
              <input
                type="text"
                value={newAnnouncement.title}
                onChange={(e) => setNewAnnouncement(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter announcement title..."
                maxLength={100}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content
              </label>
              <textarea
                value={newAnnouncement.content}
                onChange={(e) => setNewAnnouncement(prev => ({ ...prev, content: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                placeholder="Enter announcement content..."
                rows={4}
                maxLength={500}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {newAnnouncement.content.length}/500 characters
              </p>
            </div>

            <div className="flex items-center space-x-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  value={newAnnouncement.priority}
                  onChange={(e) => setNewAnnouncement(prev => ({ ...prev, priority: e.target.value as 'low' | 'medium' | 'high' }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="pin-announcement"
                  checked={newAnnouncement.is_pinned}
                  onChange={(e) => setNewAnnouncement(prev => ({ ...prev, is_pinned: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="pin-announcement" className="ml-2 text-sm text-gray-7">
                  Pin to top
                </label>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="ri-check-line mr-2"></i>
                    Create Announcement
                  </>
                )}
              </button>
              
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Announcements List */}
      <div className="p-6">
        {announcements.length === 0 ? (
          <div className="text-center py-8">
            <i className="ri-megaphone-line text-4xl text-gray-400 mb-3"></i>
            <h3 className="text-lg font-medium text-gray-600 mb-2">No Announcements</h3>
            <p className="text-gray-500 text-sm">
              {isAdmin ? 'Create your first announcement to keep the team informed.' : 'Check back later for company updates.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {announcements
              .sort((a, b) => {
                // Sort by pinned first, then by date
                if (a.is_pinned && !b.is_pinned) return -1;
                if (!a.is_pinned && b.is_pinned) return 1;
                return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
              })
              .map((announcement) => (
                <div
                  key={announcement.id}
                  className={`border rounded-lg p-4 ${
                    announcement.is_pinned 
                      ? 'border-blue-300 bg-blue-50' 
                      : 'border-gray-200 bg-white'
                  } hover:shadow-md transition-shadow`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        {announcement.is_pinned && (
                          <i className="ri-pushpin-line text-blue-600"></i>
                        )}
                        <h3 className="font-semibold text-gray-900">{announcement.title}</h3>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(announcement.priority)}`}>
                          <i className={`${getPriorityIcon(announcement.priority)} mr-1`}></i>
                          {announcement.priority.charAt(0).toUpperCase() + announcement.priority.slice(1)}
                        </span>
                      </div>
                      
                      <p className="text-gray-700 text-sm mb-3 whitespace-pre-wrap">
                        {announcement.content}
                      </p>
                      
                      <div className="flex items-center text-xs text-gray-500">
                        <i className="ri-user-line mr-1"></i>
                        <span>By {announcement.created_by}</span>
                        <span className="mx-2">•</span>
                        <i className="ri-time-line mr-1"></i>
                        <span>{new Date(announcement.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    
                    {isAdmin && (
                      <button
                        onClick={() => handleDeleteAnnouncement(announcement.id)}
                        className="ml-4 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete announcement"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    )}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementsSection;
